import { Component, DestroyRef, Input, ViewChild, inject, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';

import { AuthService } from '../../../auth/services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { filter } from 'rxjs';
import { MembershipService } from '../../../my-membership/services/membershipguard.service';
import { Member } from '../../../my-membership/models/member.type';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from '../../../../shared/services/theme.service';

type MobileNavItem = {
  text: string;
  link: string;
  icon?: string;
  cssClass?: string;
  external?: boolean;
};

type MobileNavCategory = {
  id: string;
  text: string;
  items: MobileNavItem[];
};

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatSelectModule,
    MatOptionModule,
    MatDividerModule,
    CommonModule,
  ],
})
export class HeaderComponent {
  @Input() publicMode = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly membershipService = inject(MembershipService);
  private readonly themeService = inject(ThemeService);
  @ViewChild('mobileMenuTrigger') mobileMenuTrigger?: MatMenuTrigger;
  selectedTheme = this.themeService.selectedTheme;
  breadcrumbs: string[] = [];
  private readonly memberInfoSignal = toSignal(this.membershipService.member$, { initialValue: null });
  private readonly memberLoadingSignal = toSignal(this.membershipService.memberLoading$, { initialValue: false });

  get memberInfo(): Member | null {
    return this.memberInfoSignal();
  }

  get memberInitials(): string {
    return this.getInitials(this.memberInfo?.fullName);
  }

  get isMemberInfoLoading(): boolean {
    return this.memberLoadingSignal() && !this.memberInitials;
  }

  get showAvatarFallbackIcon(): boolean {
    return !this.isMemberInfoLoading && !this.memberInitials;
  }

  private mq = window.matchMedia('(min-width: 768px)');

  mobileMenuLevel: 'root' | 'category' = 'root';
  activeMobileCategoryId: string | null = null;

  readonly mobileMenuHomeItem: MobileNavItem = {
    text: 'Forside',
    link: '/',
    icon: 'home',
  };

  readonly mobileMenuCategories: MobileNavCategory[] = [
    {
      id: 'membership',
      text: 'Medlemskap',
      items: [
        { text: 'Mitt medlemskap', link: '/mitt-medlemskap' },
        { text: 'Min kontingent', link: '/kontingent' },
        { text: 'Min fagforening', link: '/fagforening' },
      ],
    },
    {
      id: 'work',
      text: 'Arbeidsforhold',
      items: [
        { text: 'Mitt arbeidsforhold', link: '/mitt-medlemskap' },
        { text: 'Min tariffavtale', link: '/tariffavtale' },
        { text: 'Min tillitsvalgt', link: '/tillitsvalgt' },
      ],
    },
    {
      id: 'benefits',
      text: 'Fordeler og støtte',
      items: [
        { text: 'Forsikringer', link: '/forsikringer' },
        { text: 'Stipend', link: '/stipend' },
        { text: 'Reiseregninger', link: '/reiseregning' },
        { text: 'Rettigheter og fordeler', link: '/rettigheter-og-fordeler' },
      ],
    },
    {
      id: 'engagement',
      text: 'Engasjement',
      items: [
        { text: 'Verving', link: '/verving' },
        {
          text: 'Kurs og konferanser',
          link: 'https://www.fagforbundet.no/kurs-og-konferanser/',
          external: true,
        },
      ],
    },
  ];

  get activeMobileCategory(): MobileNavCategory | null {
    if (!this.activeMobileCategoryId) {
      return null;
    }

    return this.mobileMenuCategories.find((category) => category.id === this.activeMobileCategoryId) ?? null;
  }

  constructor(
    public authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.router.events;
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        let route = this.route.root;
        const breadcrumbs: string[] = [];

        const shellChildren = this.router.config.find((r) => r.path === '' && r.children)?.children ?? [];

        while (route.firstChild) {
          route = route.firstChild;

          const crumb = route.snapshot.data['breadcrumb'];
          if (crumb) breadcrumbs.push(crumb);

          const parentPath = route.snapshot.data['parent'];
          if (parentPath) {
            const parentRoute = shellChildren.find((r) => r.path === parentPath);
            const parentCrumb = parentRoute?.data?.['breadcrumb'];

            if (parentCrumb && !breadcrumbs.includes(parentCrumb)) {
              breadcrumbs.unshift(parentCrumb);
            }
          }
        }

        this.breadcrumbs = breadcrumbs;
      });
  }

  ngAfterViewInit() {
    const handler = () => {
      if (this.mq.matches) {
        this.mobileMenuTrigger?.closeMenu();
      }
    };
    handler();

    this.mq.addEventListener('change', handler);
    (this as any)._mqHandler = handler;
  }

  ngOnDestroy() {
    const handler = (this as any)._mqHandler as ((e?: any) => void) | undefined;
    if (handler) this.mq.removeEventListener('change', handler);
  }

  resetMobileMenu(): void {
    this.mobileMenuLevel = 'root';
    this.activeMobileCategoryId = null;
  }

  openMobileCategory(categoryId: string, event: Event): void {
    event.stopPropagation();
    this.activeMobileCategoryId = categoryId;
    this.mobileMenuLevel = 'category';
  }

  backToMobileRoot(event: Event): void {
    event.stopPropagation();
    this.resetMobileMenu();
  }

  async navigateMobileItem(item: MobileNavItem, event: Event): Promise<void> {
    event.stopPropagation();
    this.mobileMenuTrigger?.closeMenu();
    this.resetMobileMenu();

    if (item.external) {
      window.open(item.link, '_blank', 'noopener');
      return;
    }

    await this.router.navigateByUrl(item.link);
  }

  getInitials(name: string | null | undefined): string {
    if (!name) return '';
    const parts = name.split(' ');
    return parts
      .map((p) => p[0])
      .join('')
      .toUpperCase();
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  get isDarkTheme(): boolean {
    return this.selectedTheme() === 'dark';
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  logout() {
    this.authService.logout();
  }
}
